import { useState, useEffect } from 'react';
import { auth, db, isFirebaseMock } from '../lib/firebase';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, LogOut, Loader2, Users, Calendar, AlertTriangle, Trash2, CheckCircle2, XCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const MOCK_MEETINGS = [
  { id: '1', fullName: 'Ananya Sharma', email: 'ananya@stylesutra.in', phone: '9876543210', company: 'StyleSutra', date: '2026-07-15', time: '14:30', message: 'E-commerce redesign discussion', status: 'pending', createdAt: new Date().toISOString() },
  { id: '2', fullName: 'Vikram Nair', email: 'vikram@buildright.com', phone: '9988776655', company: 'BuildRight', date: '2026-07-12', time: '11:00', message: 'Lead gen site requirements', status: 'confirmed', createdAt: new Date().toISOString() },
  { id: '3', fullName: 'Sneha Joshi', email: 'sneha@mindfulme.app', phone: '9123456789', company: 'MindfulMe', date: '2026-07-10', time: '16:00', message: 'Wellness app UI review', status: 'completed', createdAt: new Date().toISOString() }
];

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Stats & Data states
  const [meetings, setMeetings] = useState([]);
  const [activeVisitors, setActiveVisitors] = useState(0);

  // Check authentication state
  useEffect(() => {
    if (isFirebaseMock) {
      // Mock Auth State
      const logged = localStorage.getItem('elvyen_admin_mock_logged') === 'true';
      if (logged) {
        setUser({ email: 'admin@elvyen.com', uid: 'mock-admin' });
        setMeetings(MOCK_MEETINGS);
      }
      setLoading(false);
    } else {
      // Real Firebase Auth
      const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
        setUser(currentUser);
        setLoading(false);
      });
      return () => unsubscribe();
    }
  }, []);

  // Fetch real-time data from Firestore (only if logged in and not in mock mode)
  useEffect(() => {
    if (!user || isFirebaseMock) return;

    const q = query(collection(db, 'meetings'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      setMeetings(list);
    }, (error) => {
      console.error("Firestore read error:", error);
    });

    return () => unsubscribe();
  }, [user]);

  // Fetch live active visitors from backend
  useEffect(() => {
    if (!user) return;

    const fetchVisitors = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/visitors`);
        setActiveVisitors(response.data.visitors);
      } catch (err) {
        console.error("Failed to query live visitor count:", err);
      }
    };

    fetchVisitors();
    const interval = setInterval(fetchVisitors, 10000); // refresh every 10s
    return () => clearInterval(interval);
  }, [user]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setIsLoggingIn(true);

    if (isFirebaseMock) {
      // Mock authenticate
      setTimeout(() => {
        if (email === 'robinjain142001@gmail.com' && password === 'elvyen@robin0556') {
          localStorage.setItem('elvyen_admin_mock_logged', 'true');
          setUser({ email: 'robinjain142001@gmail.com', uid: 'mock-admin' });
          setMeetings(MOCK_MEETINGS);
        } else {
          setAuthError('Invalid credentials. Use: robinjain142001@gmail.com / elvyen@robin0556');
        }
        setIsLoggingIn(false);
      }, 8000);
    } else {
      // Real authenticate
      try {
        await signInWithEmailAndPassword(auth, email, password);
      } catch (err) {
        setAuthError(err.message || 'Login failed. Please verify credentials.');
      } finally {
        setIsLoggingIn(false);
      }
    }
  };

  const handleLogout = async () => {
    if (isFirebaseMock) {
      localStorage.removeItem('elvyen_admin_mock_logged');
      setUser(null);
    } else {
      await signOut(auth);
    }
  };

  const handleStatusUpdate = async (meetingId, newStatus) => {
    if (isFirebaseMock) {
      setMeetings((prev) =>
        prev.map((m) => (m.id === meetingId ? { ...m, status: newStatus } : m))
      );
    } else {
      try {
        const meetingRef = doc(db, 'meetings', meetingId);
        await updateDoc(meetingRef, { status: newStatus });
      } catch (err) {
        console.error("Failed to update status:", err);
      }
    }
  };

  const handleMeetingDelete = async (meetingId) => {
    if (isFirebaseMock) {
      setMeetings((prev) => prev.filter((m) => m.id !== meetingId));
    } else {
      try {
        await deleteDoc(doc(db, 'meetings', meetingId));
      } catch (err) {
        console.error("Failed to delete meeting:", err);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#030303]">
        <Loader2 className="w-10 h-10 text-cyan-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] pt-32 pb-24 px-6 md:px-12 text-white">
      <div className="max-w-7xl mx-auto">
        <AnimatePresence mode="wait">
          {!user ? (
            /* Login Screen */
            <motion.div
              key="login"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="max-w-md mx-auto bg-white/5 border border-white/10 rounded-2xl p-8"
            >
              <div className="text-center mb-8">
                <div className="w-12 h-12 bg-cyan-500/10 border border-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck className="w-6 h-6 text-cyan-500" />
                </div>
                <h2 className="font-heading text-2xl font-bold">Admin Portal</h2>
                <p className="text-gray-400 text-sm mt-1">Authenticate to access database records</p>
              </div>

              {isFirebaseMock && (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) && (
                <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex items-start gap-2.5 mb-6 text-xs text-yellow-400">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-bold">Sandbox Mode Activated:</span>
                    <p className="mt-0.5 text-gray-400">Local fallback enabled. Login with credential:</p>
                    <p className="mt-1 font-mono">User: robinjain142001@gmail.com / Pass: elvyen@robin0556</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-5 py-3 bg-black/60 border border-white/10 rounded-xl focus:border-cyan-500 focus:outline-none transition-colors"
                    placeholder="robinjain142001@gmail.com"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full px-5 py-3 bg-black/60 border border-white/10 rounded-xl focus:border-cyan-500 focus:outline-none transition-colors"
                    placeholder="••••••••"
                  />
                </div>

                {authError && (
                  <p className="text-red-400 text-xs text-center border border-red-500/20 bg-red-500/5 p-3 rounded-lg">{authError}</p>
                )}

                <button
                  type="submit"
                  disabled={isLoggingIn}
                  className="w-full py-4 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                >
                  {isLoggingIn ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign In'}
                </button>
              </form>
            </motion.div>
          ) : (
            /* Dashboard Screen */
            <motion.div
              key="dashboard"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              {/* Top Bar */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-white/10 pb-6">
                <div>
                  <h1 className="font-heading text-4xl font-bold flex items-center gap-3">
                    Dashboard
                    {isFirebaseMock && (
                      <span className="text-xs bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 px-3 py-1 rounded-full uppercase font-mono tracking-wider">
                        Sandbox
                      </span>
                    )}
                  </h1>
                  <p className="text-gray-400 text-sm mt-1">Logged in as {user.email}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="px-5 py-2.5 bg-white/5 border border-white/10 hover:border-red-500/40 rounded-full text-sm font-medium flex items-center gap-2 transition-all hover:text-red-400"
                >
                  <LogOut size={16} />
                  Log Out
                </button>
              </div>

              {/* Stats Bento Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Active Visitors */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6 flex items-center gap-5">
                  <div className="w-12 h-12 bg-cyan-500/10 border border-cyan-500/20 rounded-xl flex items-center justify-center text-cyan-500">
                    <Users size={24} className="animate-pulse" />
                  </div>
                  <div>
                    <span className="text-xs uppercase text-gray-500 tracking-wider font-mono">Live Visitors</span>
                    <h3 className="text-3xl font-heading font-bold mt-0.5">{activeVisitors}</h3>
                  </div>
                </div>

                {/* Total Consultations */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6 flex items-center gap-5">
                  <div className="w-12 h-12 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center justify-center text-blue-500">
                    <Calendar size={24} />
                  </div>
                  <div>
                    <span className="text-xs uppercase text-gray-500 tracking-wider font-mono">Total Meetings</span>
                    <h3 className="text-3xl font-heading font-bold mt-0.5">{meetings.length}</h3>
                  </div>
                </div>

                {/* Pending Requests */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6 flex items-center gap-5">
                  <div className="w-12 h-12 bg-yellow-500/10 border border-yellow-500/20 rounded-xl flex items-center justify-center text-yellow-500">
                    <AlertTriangle size={24} />
                  </div>
                  <div>
                    <span className="text-xs uppercase text-gray-500 tracking-wider font-mono">Pending Decisions</span>
                    <h3 className="text-3xl font-heading font-bold mt-0.5">
                      {meetings.filter(m => m.status === 'pending').length}
                    </h3>
                  </div>
                </div>
              </div>

              {/* Meeting List Table */}
              <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden shadow-xl">
                <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
                  <h2 className="font-heading text-xl font-bold">Consultation Schedule Requests</h2>
                </div>

                {meetings.length === 0 ? (
                  <div className="p-12 text-center text-gray-500 font-mono text-sm">
                    No consultation records found in database.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-white/[0.02] border-b border-white/10 text-xs font-mono uppercase tracking-wider text-gray-400">
                          <th className="py-4 px-6">Client Details</th>
                          <th className="py-4 px-6">Company / Project</th>
                          <th className="py-4 px-6">Schedule Date & Time</th>
                          <th className="py-4 px-6">Brief Details</th>
                          <th className="py-4 px-6">Status</th>
                          <th className="py-4 px-6 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {meetings.map((m) => (
                          <tr key={m.id} className="hover:bg-white/[0.01] transition-colors text-sm">
                            <td className="py-4 px-6">
                              <div className="font-bold text-white">{m.fullName}</div>
                              <div className="text-xs text-gray-400 mt-0.5">{m.email}</div>
                              <div className="text-xs text-gray-500 mt-0.5">{m.phone}</div>
                            </td>
                            <td className="py-4 px-6">
                              <span className="px-3 py-1 bg-white/5 border border-white/10 rounded-lg text-xs font-mono">
                                {m.company || 'Not provided'}
                              </span>
                            </td>
                            <td className="py-4 px-6 font-mono text-xs">
                              <div className="text-cyan-500 font-semibold">{m.date}</div>
                              <div className="text-gray-400 mt-0.5">{m.time}</div>
                            </td>
                            <td className="py-4 px-6 max-w-xs">
                              <p className="text-xs text-gray-400 line-clamp-2">{m.message || 'No description provided'}</p>
                            </td>
                            <td className="py-4 px-6">
                              <span
                                className={`
                                  px-2.5 py-1 rounded-full text-xs font-semibold font-mono uppercase tracking-wide
                                  ${m.status === 'pending' ? 'bg-yellow-500/10 border border-yellow-500/20 text-yellow-400' : ''}
                                  ${m.status === 'confirmed' ? 'bg-blue-500/10 border border-blue-500/20 text-blue-400' : ''}
                                  ${m.status === 'completed' ? 'bg-green-500/10 border border-green-500/20 text-green-400' : ''}
                                `}
                              >
                                {m.status}
                              </span>
                            </td>
                            <td className="py-4 px-6 text-right">
                              <div className="flex items-center justify-end gap-2.5">
                                {m.status === 'pending' && (
                                  <button
                                    onClick={() => handleStatusUpdate(m.id, 'confirmed')}
                                    className="p-2 hover:bg-blue-500/15 border border-transparent hover:border-blue-500/30 rounded-lg text-blue-400 transition-all"
                                    title="Confirm Appointment"
                                  >
                                    <CheckCircle2 size={16} />
                                  </button>
                                )}
                                {m.status === 'confirmed' && (
                                  <button
                                    onClick={() => handleStatusUpdate(m.id, 'completed')}
                                    className="p-2 hover:bg-green-500/15 border border-transparent hover:border-green-500/30 rounded-lg text-green-400 transition-all"
                                    title="Mark Complete"
                                  >
                                    <CheckCircle2 size={16} />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleMeetingDelete(m.id)}
                                  className="p-2 hover:bg-red-500/15 border border-transparent hover:border-red-500/30 rounded-lg text-red-400 transition-all"
                                  title="Delete Request"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AdminDashboard;
